/** The quick actions every value on a CATALOG SATELLITE page offers — the
 *  activity and its category, the itinerary, the bus stop, the line set, the
 *  region, the trip type, the pre-trip item and its category. (The tour, the
 *  biggest member of the same family, has its own `features/tours/tourLinks`;
 *  these seven pages share one module because they share one vocabulary —
 *  tenant-scoped catalogs, `custom_id`, access tags, lat/lng, a picker per
 *  relation — and one contract test then covers all of them.)
 *
 *  Every href comes from `@/utils/gridLinks`; every action from `act.*`.
 *  Id spaces and page support, verified against the namespace files and the
 *  list pages' `FilterDef[]` (see the plan's inventory, Part 2):
 *   - /activities takes PLAIN params for `tenant` / `type` (int) / `category`
 *     (int), and `?q=` for country / currency / price_type / custom_id.
 *   - /ext-ids `model` accepts tour / itinerary / activity only — a REGION or
 *     TRIP TYPE custom id has no mapping grid, so those ids only copy.
 *   - /pre-trip-items is two grids behind `?view=`: `item_type` belongs to the
 *     Items tab (the default) and `rendering_type` to the Categories tab, so a
 *     rendering-type link MUST carry `view=categories` or it lands on Items
 *     with the param ignored.
 *   - /tours `trip_type=` is the tour/bus discriminator, NOT a Trip_Type: a
 *     trip type's tours are unreachable until /itineraries takes `trip_type=`.
 *   - Access tags (`atag`) have no console route at all — they are never
 *     linked, on any of these pages.
 */
import { tagChipActions } from '@/components/TagBadgeSet/tagChipActions';
import { act, type ValueAction } from '@/components/ValueActions/ValueActions';
import { externalUrl, looksLikeUrl } from '@/utils/externalUrl';
import {
  activitiesHref,
  activityCategoriesHref,
  bookingsHref,
  cond,
  extIdsHref,
  idCond,
  itinerariesHref,
  preTripItemsHref,
  regionsHref,
  tripTypesHref,
} from '@/utils/gridLinks';

import { CATALOG_STATUS_LABEL, type CatalogStatus } from './words';

export interface CatalogCtx {
  /** Scopes the grid links (`?tenant=`). Catalog records may be orphaned, so
   *  this is routinely null — the links then stay unscoped rather than absent. */
  tenantId: number | null | undefined;
}

const tenantOf = (ctx: CatalogCtx) => ctx.tenantId ?? null;

// ---- Shared shapes --------------------------------------------------------

/** A coordinate pair: Google Maps plus the pair on the clipboard. */
export function latLngActions(where: { lat: number; lng: number }): ValueAction[] {
  return [act.maps(where), act.copy(`${where.lat},${where.lng}`, 'Copy coordinates')];
}

/** A free-text address (searched, not geocoded). */
export function addressActions(address: string): ValueAction[] {
  return [act.maps(address), act.copy(address, 'Copy address')];
}

/** An editable web address: opened only when it looks like one, and always as
 *  an absolute URL (a scheme-less `example.com` would otherwise render a
 *  router link and 404 inside the SPA). */
export function webUrlActions(value: string | null | undefined, label = 'Open'): ValueAction[] {
  if (!looksLikeUrl(value)) return [];
  const v = value.trim();
  return [act.external(externalUrl(v), label), act.copy(v, 'Copy URL')];
}

/** A value with nowhere to go — the id is all there is to offer. */
export function copyActions(value: string, label: string): ValueAction[] {
  return value.trim() === '' ? [] : [act.copy(value.trim(), label)];
}

/** A catalog tag (itinerary / bus stop / line set): the standard tag chip set
 *  with the TOUR relations, because these are the operator's group-tour tags
 *  under a different owner. */
export function catalogTagActions(tag: { id: number; name?: string | null }, ctx: CatalogCtx): ValueAction[] {
  return tagChipActions(
    { id: tag.id, name: tag.name ?? `#${tag.id}`, category: null },
    { tenantId: tenantOf(ctx), owner: 'tour' },
  );
}

// ---- Activity -------------------------------------------------------------

/** The header's content-type chip → that type's activities for this operator
 *  (a plain `type=<int>` param, which is what the grid's toolbar reads). */
export function activityTypeActions(type: number, label: string, ctx: CatalogCtx): ValueAction[] {
  return [
    act.filter(activitiesHref([], tenantOf(ctx), { type }), `${label} activities of this operator`),
  ];
}

export function activityCategoryActions(
  category: { id: number; title?: string | null },
  ctx: CatalogCtx,
): ValueAction[] {
  const out: ValueAction[] = [
    act.open(`/activity-categories/${category.id}`, 'Open category'),
    act.filter(
      activitiesHref([], tenantOf(ctx), { category: category.id }),
      'Activities in this category',
    ),
    act.filter(activityCategoriesHref({ tenant: tenantOf(ctx) }), "Operator's activity categories"),
  ];
  if (category.title) out.push(act.copy(category.title, 'Copy category title'));
  return out;
}

export function activityCountryActions(country: { name: string }, ctx: CatalogCtx): ValueAction[] {
  return [
    act.filter(activitiesHref([cond('country', country.name)], tenantOf(ctx)), 'Activities in this country'),
    act.copy(country.name, 'Copy country'),
  ];
}

export function activityCurrencyActions(code: string, ctx: CatalogCtx): ValueAction[] {
  return [
    act.filter(activitiesHref([cond('currency', code)], tenantOf(ctx)), 'Activities priced in this currency'),
    act.copy(code, 'Copy currency code'),
  ];
}

export function activityPriceTypeActions(label: string, ctx: CatalogCtx): ValueAction[] {
  return [act.filter(activitiesHref([cond('price_type', label)], tenantOf(ctx)), 'Activities priced this way')];
}

/** The Google place id: the place on the map, and the id itself. */
export function placeIdActions(placeId: string): ValueAction[] {
  return [
    act.external(
      `https://www.google.com/maps/place/?q=place_id:${encodeURIComponent(placeId)}`,
      'Open the place in Google Maps',
    ),
    act.copy(placeId, 'Copy place id'),
  ];
}

export function activityCustomIdActions(value: string, ctx: CatalogCtx): ValueAction[] {
  const t = tenantOf(ctx);
  return [
    act.copy(value, 'Copy custom id'),
    act.filter(
      extIdsHref([cond('model', 'activity'), cond('value', value)], t),
      'External-ID mappings with this value',
    ),
    act.filter(activitiesHref([cond('custom_id', value)], t), 'Activities with this custom id'),
  ];
}

/** Every mapping that points AT this activity (`model` + `target`, the pair
 *  the ext-ids grid indexes — `target` takes the id as an all-digits value). */
export function activityExtIdsActions(activityId: number, ctx: CatalogCtx): ValueAction[] {
  return [
    act.filter(
      extIdsHref([cond('model', 'activity'), cond('target', String(activityId))], tenantOf(ctx)),
      'External-ID mappings of this activity',
    ),
  ];
}

/** An attraction row on a bus stop: attractions ARE activities (type 5). */
export function attractionActions(attraction: { id: number; name?: string | null }): ValueAction[] {
  const out: ValueAction[] = [act.open(`/activities/${attraction.id}`, 'Open attraction')];
  if (attraction.name) out.push(act.copy(attraction.name, 'Copy name'));
  return out;
}

// ---- Activity category ----------------------------------------------------

/** The category page's activity count → that category's activities. */
export function categoryActivitiesActions(categoryId: number, ctx: CatalogCtx): ValueAction[] {
  const t = tenantOf(ctx);
  return [
    act.filter(activitiesHref([], t, { category: categoryId }), 'Activities in this category'),
  ];
}

// ---- Itinerary ------------------------------------------------------------

/** The status chip's filter: the operator's itineraries the grid's `published` filter puts
 *  this one with, named in the grid filter's words — "Published", and the status word "Not
 *  published" (never "Draft", which means an unsaved edit). The grid cannot tell a missing
 *  catalog itinerary or a placeholder apart, so those say what the list holds (a
 *  placeholder's own flag is meaningless to the app: the list is unfiltered). */
const STATUS_FILTER: Readonly<Record<CatalogStatus, { published?: boolean; label: string }>> = {
  live: { published: true, label: 'Published itineraries of this operator' },
  coming_soon: {
    published: true,
    label: 'Published itineraries of this operator, live or coming soon',
  },
  not_published: { published: false, label: `${CATALOG_STATUS_LABEL.not_published} itineraries of this operator` },
  placeholder: { label: 'All itineraries of this operator' },
};

/** An itinerary's status chip (`CatalogStatusChip`) → the operator's itineraries in that
 *  state. The record header and the detail page's header both offer it. */
export function itineraryStatusActions(status: CatalogStatus, ctx: CatalogCtx): ValueAction[] {
  const { published, label } = STATUS_FILTER[status];
  return [act.filter(itinerariesHref({ tenant: tenantOf(ctx), published }), label)];
}

export function itineraryCustomIdActions(value: string, ctx: CatalogCtx): ValueAction[] {
  return [
    act.copy(value, 'Copy custom id'),
    act.filter(
      extIdsHref([cond('model', 'itinerary'), cond('value', value)], tenantOf(ctx)),
      'External-ID mappings with this value',
    ),
  ];
}

export function itineraryExtIdsActions(itineraryId: number, ctx: CatalogCtx): ValueAction[] {
  return [
    act.filter(
      extIdsHref([cond('model', 'itinerary'), cond('target', String(itineraryId))], tenantOf(ctx)),
      'External-ID mappings of this itinerary',
    ),
  ];
}

/** The itinerary's call-to-action tour. */
export function ctaTourActions(tour: { id: number; name?: string | null }, ctx: CatalogCtx): ValueAction[] {
  const out: ValueAction[] = [
    act.open(`/tours/${tour.id}`, 'Open tour'),
    act.filter(bookingsHref([idCond('tour', tour.id)], tenantOf(ctx)), 'Bookings on this tour'),
  ];
  if (tour.name) out.push(act.copy(tour.name, 'Copy tour name'));
  return out;
}

// ---- Region / trip type ---------------------------------------------------

export function regionActions(region: { id: number; name?: string | null }, ctx: CatalogCtx): ValueAction[] {
  return [
    act.open(`/regions/${region.id}`, 'Open region'),
    act.filter(regionsHref({ tenant: tenantOf(ctx) }), "Operator's regions"),
  ];
}

export function cardArrangementActions(value: string, label: string, ctx: CatalogCtx): ValueAction[] {
  return [
    act.filter(
      regionsHref({ tenant: tenantOf(ctx), card_arrangement: value }),
      `${label} regions of this operator`,
    ),
  ];
}

/** A region's bounding box opens the map at its centre. */
export function boundingBoxActions(box: {
  north: number;
  east: number;
  south: number;
  west: number;
}): ValueAction[] {
  const centre = { lat: (box.north + box.south) / 2, lng: (box.east + box.west) / 2 };
  return [
    act.maps(centre),
    act.copy(`${box.north},${box.east},${box.south},${box.west}`, 'Copy N,E,S,W'),
  ];
}

export function tripTypeActions(tripType: { id: number; name?: string | null }, ctx: CatalogCtx): ValueAction[] {
  return [
    act.open(`/trip-types/${tripType.id}`, 'Open trip type'),
    act.filter(tripTypesHref({ tenant: tenantOf(ctx) }), "Operator's trip types"),
  ];
}

// ---- Bus stop / line set --------------------------------------------------

/** A stop on a route: its page, and the map when the link carries coordinates. */
export function busStopActions(stop: {
  id: number;
  latitude?: number | null;
  longitude?: number | null;
}): ValueAction[] {
  const out: ValueAction[] = [act.open(`/bus-stops/${stop.id}`, 'Open bus stop')];
  if (stop.latitude != null && stop.longitude != null) {
    out.push(act.maps({ lat: stop.latitude, lng: stop.longitude }));
  }
  return out;
}

// ---- Pre-trip item --------------------------------------------------------

export function preTripCategoryActions(
  category: { id: number; name?: string | null },
  ctx: CatalogCtx,
): ValueAction[] {
  const out: ValueAction[] = [
    act.open(`/pre-trip-item-categories/${category.id}`, 'Open category'),
    act.filter(
      preTripItemsHref({ tenant: tenantOf(ctx), item_type: category.id }),
      'Items in this category',
    ),
  ];
  if (category.name) out.push(act.copy(category.name, 'Copy category name'));
  return out;
}

/** The count on a category page → that category's items. */
export function categoryItemsActions(categoryId: number, ctx: CatalogCtx): ValueAction[] {
  return [
    act.filter(
      preTripItemsHref({ tenant: tenantOf(ctx), item_type: categoryId }),
      'Items in this category',
    ),
  ];
}

/** The rendering-type chip → the CATEGORIES tab (`view=categories`), where
 *  `rendering_type` is a filter; on the default Items tab it is ignored. */
export function renderingTypeActions(
  renderingType: number,
  label: string,
  ctx: CatalogCtx,
): ValueAction[] {
  return [
    act.filter(
      preTripItemsHref({ tenant: tenantOf(ctx), view: 'categories', rendering_type: renderingType }),
      `${label} categories of this operator`,
    ),
  ];
}
